'use client';

import { useEffect, useState } from 'react';

/** Availability for the current style-color from Artlabs checkAvailability. */
export interface IArtlabsAvailability {
  /** Whether AR is available for the current styleColor. */
  ar: boolean;
  /** Whether VTO is available for the current styleColor. */
  vto: boolean;
}

export const DEFAULT_AVAILABILITY: IArtlabsAvailability = {
  ar: false,
  vto: false
};

/** Result of {@link useArtlabsAvailability}. */
export interface IArtlabsAvailabilityResult {
  /** Last resolved availability; may be stale until `isFetching` is false after a styleColor change. */
  availability: IArtlabsAvailability;
  /** True while `checkAvailability` is in flight for the current `styleColor`. */
  isFetching: boolean;
}

/**
 * Fetches and holds availability for a given styleColor.
 * In-flight requests are ignored if `styleColor` changes before they resolve.
 * @param styleColor - The styleColor to check availability for.
 * @returns Availability and a flag indicating whether a fetch is in progress.
 */
export function useArtlabsAvailability(
  styleColor: string
): IArtlabsAvailabilityResult {
  const [availability, setAvailability] =
    useState<IArtlabsAvailability>(DEFAULT_AVAILABILITY);
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    const api = window.ArtlabsExperience;

    if (!styleColor || !api?.checkAvailability) {
      // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- Reset loading when there is nothing to fetch (missing styleColor or SDK), so the UI is not stuck in a loading state.
      setIsFetching(false);
      return () => {};
    }

    let cancelled = false;
    // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- Set loading when starting checkAvailability for this styleColor so buttons disable until the async call completes.
    setIsFetching(true);

    const fetchAvailability = async (): Promise<void> => {
      try {
        const result = await api.checkAvailability(styleColor);
        if (cancelled) return;
        setAvailability(result?.[styleColor] ?? DEFAULT_AVAILABILITY);
      } catch {
        if (cancelled) return;
        setAvailability(DEFAULT_AVAILABILITY);
      } finally {
        if (!cancelled) {
          // eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect -- Clear loading when checkAvailability finishes (success or error); finally avoids duplicating setIsFetching in try and catch.
          setIsFetching(false);
        }
      }
    };

    // eslint-disable-next-line no-void -- Floating promise; cancellation handled via cleanup.
    void fetchAvailability();

    return () => {
      cancelled = true;
    };
  }, [styleColor]);

  return { availability, isFetching };
}
